import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { RootState } from "../store";
import { consultOrderActions } from "../store/consultOrder-slice";
import { selectHasSupportedAdvisorCapability } from "../store/auth-slice";
import { getTokenUserId } from "../utils/tokenUtils";
import { useConsultOrderContext } from "../contexts/ConsultOrderContext";
import {
  getConsultPrefill,
  createConsultOrder,
} from "../services/consultOrderService";
import { createRfzoTherapyRequest } from "../services/consultRfzoService";
import {
  buildConsultOrderPayload,
  dropEditedFieldErrors,
  validateConsultOrderDraft,
  ConsultOrderValidationErrors,
} from "../utils/consultOrderPayload";
import {
  buildConsultRfzoRequestBody,
  validateConsultRfzoDraft,
  ConsultRfzoValidationErrors,
} from "../utils/consultRfzoPayload";
import { mapConsultSubmitError } from "../utils/consultOrderErrors";
import { isConsultDraftUnfinished } from "../utils/consultDraftState";
import { mapRfzoRequestError } from "../utils/consultRfzoErrors";
import { ConsultOrderPrefillResponse } from "../types/consultOrder.types";
import { ConsultRfzoTherapyResponse } from "../types/consultRfzo.types";
import { CnCOrderResponse } from "../types/api";
import {
  CONSULT_ORDER_GRACE_MS,
  KEEPALIVE_404_THRESHOLD,
  KEEPALIVE_POLL_INTERVAL_MS,
  MOUNT_PROBE_INTERVAL_MS,
  MOUNT_PROBE_MAX_MS,
  TRIGGER_POLL_INTERVAL_MS,
  TRIGGER_POLL_MAX_MS,
} from "../utils/consultOrder.constants";

// Lifecycle state machine for the consult order, ported for the ITAM-2887
// advisor portal: no fixed pharmacy (each draft carries its own pick), and the
// RFZO side only raises an empty request, so every RFZO result is terminal.
// Owns ALL lifecycle state (phase, per-consult dedupe, ordered set, grace
// timer, generation counters, AbortControllers) and drives three bounded,
// cancellable polls:
//   1. Trigger poll  (after Join)  -> open the confirmation once on first 200.
//   2. Mount probe   (refresh)     -> show the chip on 200, NEVER auto-open.
//   3. Keep-alive    (modal open)  -> detect 200->404 (debounced) -> grace/clear.

export type ConsultOrderPhase =
  | "IDLE"
  | "ARMING"
  | "PROMPT"
  | "CHIP"
  | "FORM"
  | "SUBMITTING"
  | "SUCCESS"
  | "RFZO_FORM"
  | "RFZO_SUBMITTING"
  | "RFZO_RESULT";

/** Which submit produced the SUCCESS screen (null defaults to the CnC copy). */
export type ConsultSuccessKind = "CNC" | "RFZO";

export interface UseConsultOrderLifecycle {
  phase: ConsultOrderPhase;
  /** Latest fetched prefill (drives the prompt + is hydrated into the draft on confirm). */
  prefill: ConsultOrderPrefillResponse | null;
  chipVisible: boolean;
  graceActive: boolean;
  consultEnded: boolean;
  createdOrder: CnCOrderResponse | null;
  submitError: string | null;
  validationErrors: ConsultOrderValidationErrors;
  /** Increments on each failed submit attempt so the form can focus the first invalid field. */
  submitAttemptId: number;
  lineErrors: Record<number, string>;
  // --- ITAM-2887 RFZO request state ----------------------------------------
  /** 201 result of POST /rfzo_therapy: the in-session resume anchor. */
  rfzoResult: ConsultRfzoTherapyResponse | null;
  rfzoValidationErrors: ConsultRfzoValidationErrors;
  rfzoSubmitError: string | null;
  /** Increments on each failed submitRfzo attempt (focus-first-invalid). */
  rfzoSubmitAttemptId: number;
  successKind: ConsultSuccessKind | null;
  /** Per-consult flags that drive the prompt's "Nastavi ..." labels. */
  cncFormVisited: boolean;
  rfzoFormVisited: boolean;
  openPrompt: () => void; // CHIP -> PROMPT (chip reopens the two-choice prompt)
  dismissPrompt: () => void; // PROMPT -> CHIP (decline; no re-prompt)
  confirmPrompt: () => void; // PROMPT -> FORM ("Rezervacija"; hydrates once per consult)
  closeForm: () => void; // FORM -> CHIP (PRESERVE the draft)
  closeSuccess: () => void; // SUCCESS -> IDLE (clear the draft)
  submit: () => Promise<void>; // the CnC reservation (pickup or delivery)
  dismissSubmitError: () => void;
  confirmRfzo: () => void; // PROMPT -> RFZO_FORM (hydrate; only while no result exists)
  resumeRfzo: () => void; // PROMPT -> RFZO_RESULT (requires a result)
  closeRfzoForm: () => void; // RFZO_FORM -> CHIP (draft preserved)
  closeRfzoResult: () => void; // RFZO_RESULT -> CHIP (or IDLE when the consult ended)
  submitRfzo: () => Promise<void>; // raises the empty RFZO request
  dismissRfzoSubmitError: () => void;
}

export const useConsultOrderLifecycle = (): UseConsultOrderLifecycle => {
  const dispatch = useDispatch();
  // Read directly inside async poll continuations (getState is synchronous
  // with dispatch): a ref mirrored via an effect would lag one render flush
  // behind, and those continuations can run inside exactly that gap.
  const reduxStore = useStore<RootState>();
  const ctx = useConsultOrderContext();
  const { hydrate, clearDraft } = ctx;

  const armedConsult = useSelector(
    (s: RootState) => s.consultOrder.armedConsult
  );
  const token = useSelector((s: RootState) => s.auth.token);
  // ITAM-2887: the advisor has no fixed pharmacy; the pair each submit needs
  // comes from the draft's own picker, so eligibility is capability alone.
  const hasAdvisorCapability = useSelector(selectHasSupportedAdvisorCapability);

  const consultantId = token ? getTokenUserId(token) : null;
  const eligible = consultantId != null && hasAdvisorCapability;

  const [phase, setPhase] = useState<ConsultOrderPhase>("IDLE");
  const [prefill, setPrefill] = useState<ConsultOrderPrefillResponse | null>(
    null
  );
  const [orderedConsultIds, setOrderedConsultIds] = useState<number[]>([]);
  const [createdOrder, setCreatedOrder] = useState<CnCOrderResponse | null>(
    null
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] =
    useState<ConsultOrderValidationErrors>({});
  const [submitAttemptId, setSubmitAttemptId] = useState(0);
  const [lineErrors, setLineErrors] = useState<Record<number, string>>({});
  const [graceActive, setGraceActive] = useState(false);
  const [consultEnded, setConsultEnded] = useState(false);

  // --- ITAM-2887 RFZO request state -----------------------------------------
  const [rfzoResult, setRfzoResult] =
    useState<ConsultRfzoTherapyResponse | null>(null);
  const [rfzoValidationErrors, setRfzoValidationErrors] =
    useState<ConsultRfzoValidationErrors>({});
  const [rfzoSubmitError, setRfzoSubmitError] = useState<string | null>(null);
  const [rfzoSubmitAttemptId, setRfzoSubmitAttemptId] = useState(0);
  const [successKind, setSuccessKind] = useState<ConsultSuccessKind | null>(
    null
  );
  const [cncFormVisited, setCncFormVisited] = useState(false);
  const [rfzoFormVisited, setRfzoFormVisited] = useState(false);

  // Refs read inside timers to avoid stale closures / extra effect deps.
  const phaseRef = useRef(phase);
  const orderedRef = useRef<Set<number>>(new Set());
  const genRef = useRef(0);
  const promptedRef = useRef<number | null>(null);
  const keepAlive404Ref = useRef(0);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submitAbortRef = useRef<AbortController | null>(null);
  const draftRef = useRef(ctx.draft);
  const rfzoDraftRef = useRef(ctx.rfzoDraft);
  const ctxPrefillRef = useRef(ctx.prefill);
  // The RFZO mirrors below are updated SYNCHRONOUSLY at their write sites (not
  // via a passive effect like phaseRef) so a same-tick read in submit()/guards
  // can never see a stale value.
  const rfzoResultRef = useRef<ConsultRfzoTherapyResponse | null>(null);
  /** ConsultId all per-consult UI state (result, visited flags) belongs to. */
  const stateConsultIdRef = useRef<number | null>(null);
  /**
   * Synchronous re-entry lock shared by submitRfzo and the RFZO branch of
   * submit(). The CnC phaseRef guard is EFFECT-updated, which leaves a
   * same-tick double-fire window, and this ref closes it (set before any await,
   * cleared in finally).
   */
  const submitLockRef = useRef(false);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  useEffect(() => {
    orderedRef.current = new Set(orderedConsultIds);
  }, [orderedConsultIds]);
  useEffect(() => {
    draftRef.current = ctx.draft;
    rfzoDraftRef.current = ctx.rfzoDraft;
    ctxPrefillRef.current = ctx.prefill;
  });

  // The order's field errors are computed on submit only. An edit makes the
  // error of the edited field stale, and a pharmacy change makes every field
  // error stale, so drop them as the draft changes (no re-validation here).
  const currentDraft = ctx.draft;
  const prevDraftRef = useRef(currentDraft);
  useEffect(() => {
    const prev = prevDraftRef.current;
    prevDraftRef.current = currentDraft;
    if (prev !== currentDraft) {
      setValidationErrors((errors) =>
        dropEditedFieldErrors(errors, prev, currentDraft)
      );
    }
  }, [currentDraft]);

  const clearGraceTimer = useCallback(() => {
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
  }, []);

  /**
   * ITAM-2614: clear every piece of per-consult RFZO/UI state. Called from
   * resetToIdle and from the consult-keying guards (arm effect / mount probe)
   * when the active consultId changes. Without the latter, patient A's RFZO
   * request result could surface inside patient B's consult.
   */
  const clearRfzoState = useCallback(() => {
    stateConsultIdRef.current = null;
    rfzoResultRef.current = null;
    setRfzoResult(null);
    setSuccessKind(null);
    setCncFormVisited(false);
    setRfzoFormVisited(false);
    setRfzoSubmitError(null);
    setRfzoValidationErrors({});
  }, []);

  const resetToIdle = useCallback(() => {
    clearGraceTimer();
    genRef.current += 1; // invalidate any in-flight poll
    promptedRef.current = null;
    keepAlive404Ref.current = 0;
    if (submitAbortRef.current) {
      submitAbortRef.current.abort();
      submitAbortRef.current = null;
    }
    setConsultEnded(false);
    setGraceActive(false);
    setPrefill(null);
    setSubmitError(null);
    setValidationErrors({});
    setLineErrors({});
    setCreatedOrder(null);
    clearRfzoState();
    clearDraft();
    setPhase("IDLE");
    dispatch(consultOrderActions.clearArmed());
  }, [clearGraceTimer, clearRfzoState, clearDraft, dispatch]);

  const onConsultInactive = useCallback(() => {
    // ITAM-2622 (3.6a): the SAME rule the logout gate uses: one function
    // decides both whether the grace window is worth opening and whether logout
    // must warn. A draft is "started" once the form has been entered (context
    // prefill is hydrated); it stays started after the form is closed back to
    // the chip, so it must never be silently destroyed on consult end.
    //
    // This caller keeps reading REFS, and that is not a contradiction with the
    // reactive publisher below: `onConsultInactive` is a useCallback with deps
    // [clearGraceTimer, resetToIdle], invoked from a poll continuation, and the
    // keep-alive effect holds one instance until its identity changes. Feeding
    // it reactive values without touching its dep list would freeze them at the
    // render that created the callback. Shared *rule*, per-caller *inputs*.
    //
    // The refactor is behavior-preserving on every input this caller can
    // actually receive: its one call site sits inside the keep-alive tick,
    // whose active set excludes IDLE, ARMING, SUCCESS and a terminal
    // RFZO_RESULT, exactly the phases whose classification changed.
    if (
      isConsultDraftUnfinished(phaseRef.current, ctxPrefillRef.current != null)
    ) {
      // Grace window: keep the draft submittable, block a NEW draft (the arm
      // effect only fires from IDLE/CHIP), show a banner. Phase is left as-is
      // so a chip-closed draft can still be reopened and sent during grace.
      clearGraceTimer();
      setConsultEnded(true);
      setGraceActive(true);
      graceTimerRef.current = setTimeout(() => {
        resetToIdle();
      }, CONSULT_ORDER_GRACE_MS);
    } else {
      // No started draft (bare PROMPT/CHIP from a probe) -> just clear.
      resetToIdle();
    }
  }, [clearGraceTimer, resetToIdle]);

  // --- Arm reaction: Join signal -> ARMING (only from IDLE/CHIP) ------------
  useEffect(() => {
    const id = armedConsult?.consultId;
    if (id == null) {
      return;
    }
    if (orderedRef.current.has(id)) {
      return; // FE one-order-per-consult guard
    }
    if (phaseRef.current === "IDLE" || phaseRef.current === "CHIP") {
      // A new consult must never inherit the PREVIOUS consult's end-of-consult
      // state. Arming from CHIP is exactly where a grace-parked consult sits
      // (onConsultInactive leaves the phase alone and arms a 15-minute
      // resetToIdle): left running, that timer would fire mid-way through the
      // new consult and wipe its draft, and a stale consultEnded would keep
      // keep-alive switched off (so the NEW consult's end is never detected)
      // while showing its forms the "consult is over" banner from the start.
      clearGraceTimer();
      setConsultEnded(false);
      setGraceActive(false);
      // Consult-keying (ITAM-2614) is NOT a destructive wipe here: the state
      // may belong to this very consult (a re-arm), and wiping would drop an
      // already-raised RFZO request's result. Instead the
      // trigger poll IGNORES prefills for any consult other than the armed
      // one (a lagging backend can still return the previous consult) and
      // re-keys (clearing the other consult's RFZO state and context draft)
      // only when the armed consult's prefill actually settles.
      promptedRef.current = null;
      setPrefill(null);
      setPhase("ARMING");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armedConsult?.consultId]);

  // --- Trigger poll (phase ARMING) -----------------------------------------
  // ITAM-2622: `eligible` gates the ARMED -> polling transition too. Before
  // that, an armed consult started prefill polling on `consultantId` alone,
  // which would fire consult requests for a session with no pharmacy context.
  useEffect(() => {
    if (phase !== "ARMING" || consultantId == null || !eligible) {
      return;
    }
    let cancelled = false;
    let settled = false;
    let busy = false; // in-flight guard: never overlap poll requests
    let inFlight: AbortController | null = null;
    const gen = ++genRef.current;
    let elapsed = 0;

    const tick = async () => {
      if (cancelled || settled || busy) {
        return;
      }
      busy = true;
      const controller = new AbortController();
      inFlight = controller;
      try {
        const result = await getConsultPrefill(consultantId, controller.signal);
        if (cancelled || settled || gen !== genRef.current) {
          return;
        }
        if (result) {
          // Consult-keying (ITAM-2614): the arm signal names the consult the
          // pharmacist actually joined, and a lagging backend can still
          // return the PREVIOUS consult here. Adopting it would resurface
          // patient A's prompt (and their RFZO resume path) inside patient
          // B's consultation, so a mismatched prefill is ignored (nothing
          // renders during ARMING) and polling continues until the armed
          // consult surfaces or the window closes. The armed id is read LIVE
          // from the store: a re-arm dispatched while this request was in
          // flight is visible to this continuation immediately, before any
          // render/effect flush.
          const armedId =
            reduxStore.getState().consultOrder.armedConsult?.consultId ?? null;
          if (armedId != null && result.consultId !== armedId) {
            return;
          }
          settled = true;
          if (orderedRef.current.has(result.consultId)) {
            resetToIdle();
            return;
          }
          // Per-consult state is re-keyed at SETTLE time, not wiped at arm
          // time: a re-armed same consult keeps its state (and with it the
          // "Rezervacija" disable), while a different consult starts clean.
          if (
            stateConsultIdRef.current != null &&
            stateConsultIdRef.current !== result.consultId
          ) {
            clearRfzoState();
          }
          // The context draft (caller PII + the "started draft" grace signal)
          // is per-consult too: left in place, patient A's draft would grant
          // patient B's bare chip the 15-minute grace protection on consult
          // end, and A's PII would seed B's forms.
          if (
            ctxPrefillRef.current != null &&
            ctxPrefillRef.current.consultId !== result.consultId
          ) {
            clearDraft();
          }
          setPrefill(result);
          if (promptedRef.current !== result.consultId) {
            promptedRef.current = result.consultId; // auto-open ONCE
            setPhase("PROMPT");
          } else {
            setPhase("CHIP");
          }
        }
        // 404 -> keep polling until the deadline.
      } catch {
        // transient error -> keep polling until the deadline.
      } finally {
        busy = false;
        if (inFlight === controller) {
          inFlight = null;
        }
      }
    };

    tick();
    const interval = setInterval(() => {
      elapsed += TRIGGER_POLL_INTERVAL_MS;
      if (elapsed >= TRIGGER_POLL_MAX_MS) {
        cancelled = true;
        clearInterval(interval);
        if (inFlight) {
          inFlight.abort();
        }
        if (phaseRef.current === "ARMING") {
          // Exhausted with no adoptable 200. KNOWN LIMITATION: if the
          // backend kept returning the PREVIOUS consult for the whole
          // window, this reset drops the armed id, and the mount probe that
          // follows has no armed context: a still-lagging backend can then
          // surface the previous consult's CHIP (never auto-opens). That
          // needs a lag longer than TRIGGER_POLL_MAX_MS, and the pre-fix
          // behavior (instant adoption with an auto-opened prompt) was
          // strictly worse; no extra machinery for it by design.
          resetToIdle();
        }
        return;
      }
      tick();
    }, TRIGGER_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (inFlight) {
        inFlight.abort();
      }
    };
    // The armed id is a dep so a re-arm DURING ARMING (the arm effect
    // declines outside IDLE/CHIP) restarts the poll: fresh 2-minute window
    // for the new consult, stale in-flight request aborted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, eligible, consultantId, armedConsult?.consultId]);

  // --- Mount probe (phase IDLE, eligible, not armed) -----------------------
  useEffect(() => {
    if (phase !== "IDLE" || !eligible || consultantId == null) {
      return;
    }
    if (armedConsult?.consultId != null) {
      return; // the arm path will handle it
    }
    let cancelled = false;
    let settled = false;
    let busy = false; // in-flight guard: never overlap probe requests
    let inFlight: AbortController | null = null;
    const gen = ++genRef.current;
    let elapsed = 0;

    const probe = async () => {
      if (cancelled || settled || busy) {
        return;
      }
      busy = true;
      const controller = new AbortController();
      inFlight = controller;
      try {
        const result = await getConsultPrefill(consultantId, controller.signal);
        if (cancelled || settled || gen !== genRef.current) {
          return;
        }
        if (result) {
          settled = true;
          if (orderedRef.current.has(result.consultId)) {
            return; // already ordered -> no chip
          }
          // Consult-keying (ITAM-2614), same rule as the trigger poll: the
          // probe only runs from IDLE (where resetToIdle already cleared the
          // RFZO state AND the context draft), but a different consultId must
          // never inherit either.
          if (
            stateConsultIdRef.current != null &&
            stateConsultIdRef.current !== result.consultId
          ) {
            clearRfzoState();
          }
          if (
            ctxPrefillRef.current != null &&
            ctxPrefillRef.current.consultId !== result.consultId
          ) {
            clearDraft();
          }
          setPrefill(result);
          setPhase("CHIP"); // NEVER auto-open after refresh
        }
      } catch {
        // ignore; bounded window will end the probe
      } finally {
        busy = false;
        if (inFlight === controller) {
          inFlight = null;
        }
      }
    };

    probe();
    const interval = setInterval(() => {
      elapsed += MOUNT_PROBE_INTERVAL_MS;
      if (elapsed >= MOUNT_PROBE_MAX_MS) {
        cancelled = true;
        clearInterval(interval);
        if (inFlight) {
          inFlight.abort();
        }
        return;
      }
      probe();
    }, MOUNT_PROBE_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (inFlight) {
        inFlight.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, eligible, consultantId, armedConsult?.consultId]);

  // --- ITAM-2622 (3.6a): publish the unfinished-draft signal ---------------
  // Read the REACTIVE context prefill, never `ctxPrefillRef.current`: that ref
  // is assigned by a dependency-less effect, and writing a ref schedules no
  // render, so an effect keyed on it would be keyed on something that cannot
  // wake it: it could publish a stale value indefinitely. `ctx.prefill` comes
  // from the provider, so a hydrate/clearDraft re-renders this hook.
  //
  // `useLayoutEffect`, not `useEffect`, on ordering grounds: state changes that
  // move this boolean can originate in promise continuations (the submit paths
  // and the trigger-poll settle, which can clearDraft and change the phase in
  // one continuation), which React 18 batches into a commit whose PASSIVE
  // effects flush in a later macrotask. A layout effect flushes inside the
  // commit, closing a sub-frame gap on a control whose whole job is to be safe
  // at an arbitrary moment. It costs nothing else: the dispatch is a store
  // write rather than a render, and this hook does not subscribe to the field
  // it publishes, so there is no cascade.
  const unfinishedDraft = isConsultDraftUnfinished(phase, ctx.prefill != null);
  useLayoutEffect(() => {
    // The dep is a primitive, so this runs only on a real change, and the
    // reducer assigns one boolean (immer no-ops when unchanged): no loop, no
    // dispatch-during-render.
    dispatch(consultOrderActions.publishUnfinishedDraft(unfinishedDraft));
  }, [dispatch, unfinishedDraft]);

  // --- Keep-alive poll (a modal/chip is holding a 200 prefill) -------------
  // RFZO working phases join the active set; an RFZO result is always
  // terminal here (no step-2 order) and behaves like SUCCESS (excluded, so
  // consult end can never wipe the confirmation).
  useEffect(() => {
    const active =
      phase === "PROMPT" ||
      phase === "CHIP" ||
      phase === "FORM" ||
      phase === "SUBMITTING" ||
      phase === "RFZO_FORM" ||
      phase === "RFZO_SUBMITTING";
    // Once the consult has ENDED (grace running), stop keep-alive entirely: the
    // grace timer alone owns the reset. Otherwise closing the form to the chip
    // during grace would restart keep-alive, hit the 404 threshold again, and
    // reset to IDLE, collapsing the grace window and wiping the draft early.
    // ITAM-2622: `eligible` gates the keep-alive for the same reason as the
    // trigger poll: it must never run for a session with no pharmacy context.
    if (!active || consultEnded || consultantId == null || !eligible) {
      return;
    }
    let cancelled = false;
    let busy = false; // in-flight guard: never overlap poll requests
    let inFlight: AbortController | null = null;
    const gen = ++genRef.current;
    keepAlive404Ref.current = 0;

    const tick = async () => {
      if (cancelled || busy) {
        return;
      }
      busy = true;
      const controller = new AbortController();
      inFlight = controller;
      try {
        const result = await getConsultPrefill(consultantId, controller.signal);
        if (cancelled || gen !== genRef.current) {
          return;
        }
        if (result) {
          keepAlive404Ref.current = 0; // still active
        } else {
          keepAlive404Ref.current += 1;
          if (keepAlive404Ref.current >= KEEPALIVE_404_THRESHOLD) {
            cancelled = true;
            clearInterval(interval);
            onConsultInactive();
          }
        }
      } catch {
        // transient error -> neither confirm nor count as a 404
      } finally {
        busy = false;
        if (inFlight === controller) {
          inFlight = null;
        }
      }
    };

    const interval = setInterval(tick, KEEPALIVE_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (inFlight) {
        inFlight.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, consultEnded, consultantId, eligible, onConsultInactive]);

  // --- Consultant change: full reset ---------------------------------------
  const prevConsultantRef = useRef(consultantId);
  useEffect(() => {
    if (prevConsultantRef.current !== consultantId) {
      prevConsultantRef.current = consultantId;
      setOrderedConsultIds([]);
      resetToIdle();
    }
  }, [consultantId, resetToIdle]);

  // --- Unmount cleanup -----------------------------------------------------
  useEffect(
    () => () => {
      clearGraceTimer();
      genRef.current += 1;
      if (submitAbortRef.current) {
        submitAbortRef.current.abort();
      }
      // ITAM-2622 (3.6a): a derived signal cannot un-publish itself once its
      // deriver is gone. Without this, a persona remount (3.6) would leave the
      // last `true` standing and every later logout would warn about a draft
      // that no longer exists.
      dispatch(consultOrderActions.publishUnfinishedDraft(false));
    },
    [clearGraceTimer, dispatch]
  );

  // --- Actions -------------------------------------------------------------
  const openPrompt = useCallback(() => {
    setPhase((p) => (p === "CHIP" ? "PROMPT" : p));
  }, []);

  const dismissPrompt = useCallback(() => {
    setPhase((p) => (p === "PROMPT" ? "CHIP" : p));
  }, []);

  const confirmPrompt = useCallback(() => {
    if (!prefill) {
      return;
    }
    // Hydrate the draft only on the FIRST entry to the form for THIS consult.
    // Re-entering via the chip (CHIP -> PROMPT -> confirm) must PRESERVE the
    // pharmacist's edits; re-hydrating would reset the draft to the prefill and
    // silently discard added items / delivery choices.
    if (ctxPrefillRef.current?.consultId !== prefill.consultId) {
      hydrate(prefill);
    }
    stateConsultIdRef.current = prefill.consultId;
    setCncFormVisited(true);
    setValidationErrors({});
    setSubmitError(null);
    setLineErrors({});
    setPhase("FORM");
  }, [prefill, hydrate]);

  const closeForm = useCallback(() => {
    // Preserve the draft; the chip reopens via the confirmation step.
    setPhase((p) => (p === "FORM" ? "CHIP" : p));
  }, []);

  const closeSuccess = useCallback(() => {
    resetToIdle();
  }, [resetToIdle]);

  const dismissSubmitError = useCallback(() => {
    setSubmitError(null);
    setLineErrors({});
  }, []);

  const submit = useCallback(async () => {
    if (phaseRef.current === "SUBMITTING") {
      return; // in-flight guard
    }
    if (submitLockRef.current) {
      return; // synchronous re-entry lock (the phaseRef guard is effect-updated)
    }
    const draft = draftRef.current;
    const activePrefill = ctxPrefillRef.current;
    if (!activePrefill) {
      return;
    }

    // ITAM-2887: the pair is the draft's own pick; validation reports a
    // missing one as a field error, so the null check below is defense only.
    // The delivery pre-checks (zone, fee) are enforced here, keyed on that
    // pair, so a stale approval for another pharmacy never reaches the POST.
    const { valid, errors } = validateConsultOrderDraft(draft, activePrefill, {
      enforcePrechecks: true,
    });
    if (!valid || draft.pharmacy == null) {
      setValidationErrors(errors);
      // Bump the attempt nonce so the form focuses/scrolls to the first invalid
      // field (the button itself is never pre-disabled by validity anymore).
      setSubmitAttemptId((n) => n + 1);
      return;
    }
    submitLockRef.current = true; // set BEFORE any await
    setValidationErrors({});
    setSubmitError(null);
    setLineErrors({});
    setPhase("SUBMITTING");

    const controller = new AbortController();
    submitAbortRef.current = controller;
    try {
      const payload = buildConsultOrderPayload(draft, activePrefill, {
        marketplaceId: draft.pharmacy.marketplaceId,
        branchMarketplaceId: draft.pharmacy.branchMarketplaceId,
      });
      const order = await createConsultOrder(payload, controller.signal);
      if (controller.signal.aborted) {
        return;
      }
      // The consult-end grace timer must not outlive a completed submit:
      // left armed, it would fire resetToIdle mid-SUCCESS and wipe the
      // confirmation (and created order number) out from under the pharmacist.
      clearGraceTimer();
      setCreatedOrder(order);
      setSuccessKind("CNC");
      setOrderedConsultIds((prev) =>
        prev.includes(activePrefill.consultId)
          ? prev
          : [...prev, activePrefill.consultId]
      );
      setPhase("SUCCESS");
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      const mapped = mapConsultSubmitError(error);
      if (mapped.is409) {
        // Already created -> treat as success (hide chip, mark ordered).
        clearGraceTimer(); // same as the success path: SUCCESS must not be wiped
        setOrderedConsultIds((prev) =>
          prev.includes(activePrefill.consultId)
            ? prev
            : [...prev, activePrefill.consultId]
        );
        setCreatedOrder(null);
        setSuccessKind("CNC");
        setSubmitError(mapped.message);
        setPhase("SUCCESS");
        return;
      }
      setSubmitError(mapped.message);
      if (mapped.lineErrors) {
        setLineErrors(mapped.lineErrors);
      }
      setPhase("FORM"); // keep the draft
    } finally {
      submitLockRef.current = false;
      submitAbortRef.current = null;
    }
  }, [clearGraceTimer]);

  // --- ITAM-2887 RFZO request actions ---------------------------------------

  const confirmRfzo = useCallback(() => {
    if (!prefill) {
      return;
    }
    if (rfzoResultRef.current) {
      return; // a result exists -> the prompt routes to resumeRfzo instead
    }
    // Same once-per-consult hydration guard as confirmPrompt: both flows share
    // one "draft started" signal (ctx.prefill), so entering EITHER form seeds
    // BOTH drafts exactly once per consult and re-entry preserves edits.
    if (ctxPrefillRef.current?.consultId !== prefill.consultId) {
      hydrate(prefill);
    }
    stateConsultIdRef.current = prefill.consultId;
    setRfzoFormVisited(true);
    setRfzoValidationErrors({});
    setRfzoSubmitError(null);
    setPhase("RFZO_FORM");
  }, [prefill, hydrate]);

  const resumeRfzo = useCallback(() => {
    if (!rfzoResultRef.current) {
      return; // nothing to resume
    }
    setPhase((p) => (p === "PROMPT" ? "RFZO_RESULT" : p));
  }, []);

  const closeRfzoForm = useCallback(() => {
    // Preserve the draft; the chip reopens via the two-choice prompt.
    setPhase((p) => (p === "RFZO_FORM" ? "CHIP" : p));
  }, []);

  const closeRfzoResult = useCallback(() => {
    const result = rfzoResultRef.current;
    if (consultEnded && result != null) {
      // Consult over + a (terminal) result: nothing left to do for this consult.
      resetToIdle();
      return;
    }
    // Live consult: back to the chip. Deliberately does NOT mark the consult
    // ordered: after the request a regular reservation stays possible.
    setPhase((p) => (p === "RFZO_RESULT" ? "CHIP" : p));
  }, [consultEnded, resetToIdle]);

  const submitRfzo = useCallback(async () => {
    if (phaseRef.current === "RFZO_SUBMITTING") {
      return; // in-flight guard (belt)
    }
    if (submitLockRef.current) {
      return; // synchronous re-entry lock (suspenders: closes the same-tick window)
    }
    if (rfzoResultRef.current) {
      // A request was already raised for this consult: one request per
      // consult, never re-fired from this session (a second one would be
      // refused as a duplicate anyway). The pharmacy takes it from here.
      return;
    }
    const rfzoDraft = rfzoDraftRef.current;
    const activePrefill = ctxPrefillRef.current;
    if (!activePrefill) {
      return;
    }

    const { valid, errors } = validateConsultRfzoDraft(rfzoDraft);
    if (!valid || rfzoDraft.pharmacy == null) {
      setRfzoValidationErrors(errors);
      setRfzoSubmitAttemptId((n) => n + 1);
      return;
    }
    submitLockRef.current = true; // set BEFORE any await
    setRfzoValidationErrors({});
    setRfzoSubmitError(null);
    setPhase("RFZO_SUBMITTING");

    const controller = new AbortController();
    submitAbortRef.current = controller;
    try {
      // The request belongs to the CALLER (prefill.user.id), never to the
      // advisor whose token sends it.
      const body = buildConsultRfzoRequestBody(
        rfzoDraft,
        activePrefill.user.id,
        {
          marketplaceId: rfzoDraft.pharmacy.marketplaceId,
          branchMarketplaceId: rfzoDraft.pharmacy.branchMarketplaceId,
        }
      );
      const result = await createRfzoTherapyRequest(body, controller.signal);
      if (controller.signal.aborted) {
        return;
      }
      stateConsultIdRef.current = activePrefill.consultId;
      rfzoResultRef.current = result;
      setRfzoResult(result);
      // Every result is terminal here (the advisor does nothing after the
      // request), so, as on the CnC success path, the grace timer must never
      // wipe the confirmation.
      clearGraceTimer();
      setPhase("RFZO_RESULT");
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      const mapped = mapRfzoRequestError(error, {
        lbo: rfzoDraft.lbo.trim(),
      });
      setRfzoSubmitError(mapped.message);
      setPhase("RFZO_FORM"); // keep the draft
    } finally {
      submitLockRef.current = false;
      submitAbortRef.current = null;
    }
  }, [clearGraceTimer]);

  const dismissRfzoSubmitError = useCallback(() => {
    setRfzoSubmitError(null);
  }, []);

  return useMemo(
    () => ({
      phase,
      prefill,
      chipVisible: phase === "CHIP",
      graceActive,
      consultEnded,
      createdOrder,
      submitError,
      validationErrors,
      submitAttemptId,
      lineErrors,
      rfzoResult,
      rfzoValidationErrors,
      rfzoSubmitError,
      rfzoSubmitAttemptId,
      successKind,
      cncFormVisited,
      rfzoFormVisited,
      openPrompt,
      dismissPrompt,
      confirmPrompt,
      closeForm,
      closeSuccess,
      submit,
      dismissSubmitError,
      confirmRfzo,
      resumeRfzo,
      closeRfzoForm,
      closeRfzoResult,
      submitRfzo,
      dismissRfzoSubmitError,
    }),
    [
      phase,
      prefill,
      graceActive,
      consultEnded,
      createdOrder,
      submitError,
      validationErrors,
      submitAttemptId,
      lineErrors,
      rfzoResult,
      rfzoValidationErrors,
      rfzoSubmitError,
      rfzoSubmitAttemptId,
      successKind,
      cncFormVisited,
      rfzoFormVisited,
      openPrompt,
      dismissPrompt,
      confirmPrompt,
      closeForm,
      closeSuccess,
      submit,
      dismissSubmitError,
      confirmRfzo,
      resumeRfzo,
      closeRfzoForm,
      closeRfzoResult,
      submitRfzo,
      dismissRfzoSubmitError,
    ]
  );
};
